import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { useApiClient, useFetch, OutlinedIcons } from '@marageti/z4-lib';
import { TripMatch, TripMatchStatus, SalesStage } from '@marageti/z4-sdk/lib/travel';
import { Agent } from '@marageti/z4-sdk/lib/people';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import LogCallDialog from '../zicasso-dialogs/log-call';
import AddItineraryDialog from '../zicasso-dialogs/add-itinerary';
import { getDueStatus, ReminderMap } from '../../utils/reminders';
import { calculateRemainingTime, formatFirstMessageHoursLeft } from '../../utils/helpers';
import { useAuthStore } from '../../store';

const { OutlinedInformationCircleIcon, OutlinedExclamationIcon } = OutlinedIcons;

type Reminder = {
  dueAt: string;
  index: number;
  startAt: string;
  origDueAt: string;
};

const salesStages: Record<string, { buttonType: string[] }> = {
  FIRST_MESSAGE_SENT: {
    buttonType: ['logCall'],
  },
  CONSULTATION: {
    buttonType: ['uploadItnerary'],
  },
  ITINERARY_SENT: {
    buttonType: ['logCall'],
  },
  ITINERARY_IN_REVIEW: {
    buttonType: ['logCall'],
  },
};

const defaultReminderDetails: { title: string, description: string, buttonType: string[] } = {
  title: '',
  description: '',
  buttonType: [],
};

const inactiveLeads: Record<string, { index: number }> = {
  FIRST_MESSAGE_SENT: {
    index: 9,
  },
  CONSULTATION: {
    index: 9,
  },
  ITINERARY_SENT: {
    index: 9,
  },
  ITINERARY_IN_REVIEW: {
    index: 7,
  },
};

const tripNeedsFirstMessage = (tripMatch: TripMatch | undefined, agent: Agent | null) => (
  tripMatch?.status === TripMatchStatus.MatchAccepted && agent && agent.userInfo.some((userInfo) => !userInfo.roles?.includes(RoleName.AGENT_SUPPORT))
);

const SideBarReminder = ({
  salesStage,
  reminder,
  tripMatchCreatedAt,
  tripMatchId,
}: {
  salesStage: SalesStage;
  reminder: Reminder;
  tripMatchCreatedAt: string;
  tripMatchId: string;
}) => {
  const confirm = useConfirm();
  const apiClient = useApiClient();
  const { loggedInAgent } = useAuthStore();
  const { data: tripMatch, mutate } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], true);
  const [openLogCallDialog, setOpenLogCallDialog] = useState(false);
  const [openItineraryDialog, setOpenItineraryDialog] = useState(false);
  const showFirstMessageReminder = tripNeedsFirstMessage(tripMatch, loggedInAgent);
  const { isDue: isReminderDue, isOverdue: isReminderOverdue } = getDueStatus(reminder);
  let isFirstMessageOverdue = false;
  const details = defaultReminderDetails;
  let description = (salesStages[salesStage] && (isReminderDue || isReminderOverdue)) ? ReminderMap[salesStage][reminder.index]?.longDescription : details.description;
  let { title } = details;
  if (salesStages[salesStage] && (isReminderDue || isReminderOverdue)) {
    const titlePrefix = (isReminderDue && !isReminderOverdue) ? 'Reminder: ' : 'Overdue: ';
    title = `${titlePrefix}${ReminderMap[salesStage][reminder.index]?.title || details.title}`;
    details.buttonType = salesStages[salesStage].buttonType;
  }
  if (showFirstMessageReminder) {
    const remainingTime = calculateRemainingTime(tripMatchCreatedAt);
    isFirstMessageOverdue = remainingTime === 0;
    const titlePrefix = isFirstMessageOverdue ? 'Overdue: ' : 'Reminder: ';
    title = `${titlePrefix}Send First Message${isFirstMessageOverdue ? '' : ` (${formatFirstMessageHoursLeft(remainingTime)})`}`;
    description = 'Send a message to the traveler with a brief intro about your company, itinerary ideas, and an invitation to schedule a call.';
    details.buttonType = [];
  }

  const onClickInactive = () => {
    confirm({
      title: 'Mark Trip as Inactive?',
      description: (
        <>
          Inactive trips can be reactivated in the future and remain visible to travelers, who can still send messages.
          Trips should only be marked as inactive after you have completed the follow-up timeline or the travel dates have passed.
        </>
      ),
      confirmationText: 'Confirm',
    })
      .then(async () => {
        if (tripMatchId) {
          try {
            await apiClient.tripMatchesClient.putTripMatchStatus(tripMatchId, {
              id: tripMatchId,
              status: TripMatchStatus.MatchInactive,
              statusInfo: {
                reason: 'Trip marked as inactive by Agent',
              },
            });
            mutate();
            enqueueSnackbar('Trip marked as inactive.', { variant: 'success' });
          } catch (error) {
            console.error(error);
          }
        }
      })
      .catch(() => {});
  };

  const buttonTypes: Record<string, { label: string, onClick?: () => void }> = {
    logCall: {
      label: 'Log Phone Call',
      onClick: () => {
        setOpenLogCallDialog(true);
      },
    },
    markInactive: {
      label: 'Mark as Inactive',
      onClick: () => {
        onClickInactive();
      },
    },
    uploadItnerary: {
      label: 'Upload Itinerary',
      onClick: () => {
        setOpenItineraryDialog(true);
      },
    },
  };

  const isInactiveLead = inactiveLeads[salesStage] && reminder.index === inactiveLeads[salesStage].index;
  if (isInactiveLead) {
    details.buttonType = ['markInactive'];
  }

  if (!showFirstMessageReminder && !isReminderOverdue && !isReminderDue) {
    return null;
  }

  // eslint-disable-next-line no-nested-ternary
  let backgroundColor = isReminderDue ? 'info.light' : isReminderOverdue ? 'warning.light' : 'background.paper';
  if (showFirstMessageReminder) {
    backgroundColor = isFirstMessageOverdue ? 'warning.light' : 'info.light';
  }

  return (
    <>
      <Card
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          boxShadow: 'none',
          p: 3,
          maxWidth: '100%',
          backgroundColor,
        }}
      >
        <Box sx={{ mr: 2, mt: 0.5 }}>
          { isReminderDue && !showFirstMessageReminder && <OutlinedInformationCircleIcon sx={{ color: 'info.main', fontSize: 24 }} /> }
          { isReminderOverdue && !showFirstMessageReminder && <OutlinedExclamationIcon sx={{ color: 'warning.main', fontSize: 24 }} /> }
          { showFirstMessageReminder && isFirstMessageOverdue && <OutlinedExclamationIcon sx={{ color: 'warning.main', fontSize: 24 }} /> }
          { showFirstMessageReminder && !isFirstMessageOverdue && <OutlinedInformationCircleIcon sx={{ color: 'info.main', fontSize: 24 }} /> }
        </Box>
        <CardContent sx={{ p: 0, flex: 1, pb: '0 !important' }}>
          <Typography variant="bodyBold" mb={1} lineHeight="24px" fontSize="16px" component="p">
            {title}
          </Typography>
          <Typography variant="body" mb="20px" lineHeight="24px" color="text.secondary">
            {description}
          </Typography>
          {details.buttonType.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              {details.buttonType.map((type) => (
                <Button key={type} variant="contained" color="primary" sx={{ mr: 2 }} onClick={buttonTypes[type].onClick}>
                  {buttonTypes[type].label}
                </Button>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
      <LogCallDialog
        closeDialog={() => setOpenLogCallDialog(false)}
        onSuccess={() => { mutate(); }}
        open={openLogCallDialog}
        conversationId={tripMatch?.travelerToPartnerId || ''}
      />
      <AddItineraryDialog
        closeDialog={() => setOpenItineraryDialog(false)}
        onDocumentUploaded={() => { mutate(); }}
        open={openItineraryDialog}
        tripMatchId={tripMatchId}
      />
    </>
  );
};

export default SideBarReminder;
